import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HOT_COMPARISONS } from "@/lib/hot-compare";
import { getHotCompareContent } from "@/lib/hot-compare.i18n";
import { useLocale, useT } from "@/i18n";
import { BOARDS_OG_IMAGE, BOARDS_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/compare/hot/")({
  head: () =>
    buildPageMetadata({
      path: "/compare/hot",
      title: "HOT Board Alternatives — BeHot.Now vs Paid Leaderboards",
      description:
        "Compare BeHot.Now HOT boards with Outbid.lol, Outbid.to, Outbid.quest, Winning.com, TopApp and Product Hunt: pricing, ranking rules and what sits behind a listing.",
      ogTitle: "BeHot.Now vs other paid leaderboards",
      ogDescription:
        "Side by side comparisons of the main pay to rank leaderboards, with pricing, ranking rules and sources.",
      image: BOARDS_OG_IMAGE,
      imageAlt: BOARDS_OG_IMAGE_ALT,
      keywords: [
        "pay to rank alternatives",
        "paid leaderboard comparison",
        "outbid.lol alternative",
        "product leaderboard alternatives",
      ],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "HOT", path: "/hot" },
        { name: "Comparisons", path: "/compare/hot" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "BeHot.Now HOT board comparisons",
        url: `${SITE_URL}/compare/hot`,
      },
    }),
  component: CompareHub,
});

function CompareHub() {
  const locale = useLocale();
  const { t } = useT();
  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
        <Scale className="size-3.5" /> {t("hotc.comparisons")}
      </span>
      <h1 className="mt-3 font-display text-3xl font-bold sm:text-4xl">{t("hotc.hub.h1")}</h1>
      <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
        {t("hotc.hub.intro")}
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {HOT_COMPARISONS.map((c) => (
          <Link
            key={c.slug}
            to="/compare/hot/$competitor"
            params={{ competitor: c.slug }}
            className="card-surface group p-4 transition hover:border-primary/50"
          >
            <h2 className="font-display font-bold">{getHotCompareContent(locale, c).h1}</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {getHotCompareContent(locale, c).description}
            </p>
            <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary">
              {t("hotc.hub.read")} <ArrowRight className="size-4" />
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Button asChild variant="hot">
          <Link to="/hot">{t("hotc.hub.liveBoards")}</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/hot/$topic" params={{ topic: "pricing" }}>
            {t("hotc.hub.cost")}
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/compare/marketplace">{t("mpseo.compareOthers")}</Link>
        </Button>
      </div>
    </div>
  );
}
